import React, { useContext, useEffect, useState } from "react";
import styles from "./styles/Search.module.css";
import DataContext from "../../context/DataContext";
import TicketInfo from "./TicketInfo";
import SidePanel from "./SidePanel";
import PhotoPanel from "./PhotoPanel";
import Spinner from "../../components/ui/spinner/Spinner";
import { useNavigate, useParams } from "react-router-dom";
import useCurrencySymbol from "../../hooks/useCurrencySymbol";

import PaymentFloat from "./PaymentFloat";

/**
 * Search component
 * This component handles the display of search results, showing ticket information and related details.
 */
const Search = () => {
    const { bUser } = useParams(); // Extracts 'bUser' parameter from the URL.

    const {
        currentTicket, //{Object} - Current ticket data.
        currentCustom, //{Object} - dlient customizations.
        isLoading, //{Boolean} - Loading state.
        isValidSearch, //{Boolean} - Search validity state.
        isServiceDataAvailable, //{Boolean} - Service data availability state.
        serviceData, //{Function} - Service data function.
        currentBU, //{String} - Current business user.
        paymentHeaderFooterHeight, //{Number} - Payment module calculated header and footer height.
        ticketType,
        responseData,
        setCurrentTicket,
        connection,
        selectedTickets,
        setSelectedTickets,
        selectedInstalments,
        setSelectedInstalments,
        JWTToken,
    } = useContext(DataContext);

    const currencySymbol = useCurrencySymbol(); // Custom hook to get the currency symbol based on current settings.
    const navigate = useNavigate(); // Hook for navigation in React Router.
    const [notReadyView, setNotReadyView] = useState(true);

    // Fetch service date if not available or if the business user has changed.
    useEffect(() => {
        bUser &&
            JWTToken &&
            (!isServiceDataAvailable || bUser !== currentBU) &&
            serviceData(bUser);
    }, [bUser, isServiceDataAvailable, JWTToken]);

    // useEffect to navigate to home page if the search is not valid.
    useEffect(() => {
        !isValidSearch && navigate(`/${bUser}`);
    }, [isValidSearch, navigate, bUser]);

    // Reset selected instalments and selected tickets
    useEffect(() => {
        setSelectedTickets([]);

        setSelectedInstalments([]);
    }, []);

    const [selectedSettlements, setselectedSettlements] = useState([]);

    const handleCheckInstalment = (event) => {
        event.stopPropagation(); // Prevent event bubbling

        setSelectedInstalments((prevSelected = []) => {
            // Ensure prevSelected is an array
            const isSelected = prevSelected.some(
                (inst) => inst.instalmentId === event.target.name
            );
            if (isSelected) {
                if (prevSelected.length < 2) {
                    return prevSelected;
                } else {
                    return prevSelected.filter(
                        (inst) => inst.instalmentId !== event.target.name
                    ); // Remove if exists
                }
            } else {
                return [
                    ...prevSelected,
                    {
                        instalmentId: event.target.name,
                        amount: event.target.value,
                        instalmentInfo: event.target.dataset.instinfo,
                        instalmentDate: event.target.dataset.instalmentdate,
                    },
                ];
            }
        });
    };

    const handleCheckSettlement = (event) => {
        event.stopPropagation();

        setselectedSettlements((prevSelected) =>
            prevSelected.includes(event.target.name)
                ? prevSelected.filter((caseNo) => caseNo !== event.target.name)
                : [...prevSelected, event.target.name]
        );
    };

    const [isSelectedSettlements, setIsSelectedSettlements] = useState(false);

    useEffect(() => {
        setIsSelectedSettlements(
            responseData?.settlements?.length === selectedSettlements?.length
        );
    }, [selectedSettlements]);

    const handleSelectAllSettlements = () => {
        if (!isSelectedSettlements) {
            responseData?.settlements &&
                responseData?.settlements?.length &&
                responseData?.settlements.map((item) => {
                    setselectedSettlements((prevSelected) =>
                        prevSelected.includes(item?.caseNo)
                            ? [...prevSelected]
                            : [...prevSelected, item?.caseNo]
                    );
                });
        } else {
            setselectedSettlements([]);
        }
    };
    // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
    // useEffect(() => {
    //     if (currentTicket && ticketType === 1) {
    //         setClickedSettlement(currentTicket?.caseNo);
    //     }
    // }, []);
    // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@

    useEffect(() => {
        if (isServiceDataAvailable || !isLoading) {
            const timer = setTimeout(() => {
                setNotReadyView(false);
            }, 50);

            // Cleanup function
            return () => {
                clearTimeout(timer);
            };
        }
    }, [isServiceDataAvailable, isLoading]);

    return (
        <article
            className={` main-page-container flex-container`}
            style={{
                minHeight: `calc(100vh - ${paymentHeaderFooterHeight}px)`,
            }}
        >
            {isLoading && !isServiceDataAvailable ? (
                <Spinner />
            ) : (
                isValidSearch && (
                    <>
                        <main
                            className={`${
                                styles.main_search_container
                            }  main-container v-flex-container   transition-container  ${
                                notReadyView
                                    ? "transition-hidden"
                                    : "transition-visible"
                            }`}
                        >
                            {connection?.isPayment && (
                                <div
                                    className={`${styles.payment_float_container} flex-container`}
                                >
                                    <PaymentFloat
                                        feeDetails={responseData?.feeData}
                                        selectedTickets={selectedTickets}
                                        selectedInstalments={
                                            selectedInstalments
                                        }
                                        plateNumber={
                                            responseData?.ticket?.plate ||
                                            responseData?.settlements?.[0]
                                                ?.plate
                                        }
                                    />
                                </div>
                            )}
                            <div
                                className={`${styles.main_bottom_panel} flex-container`}
                            >
                                <SidePanel
                                    selectedSettlements={selectedSettlements}
                                    selectedInstalments={selectedInstalments}
                                    setSelectedInstalments={
                                        setSelectedInstalments
                                    }
                                    handleCheckInstalment={
                                        handleCheckInstalment
                                    }
                                    handleCheckSettlement={
                                        handleCheckSettlement
                                    }
                                    isSelectedSettlements={
                                        isSelectedSettlements
                                    }
                                    handleSelectAllSettlements={
                                        handleSelectAllSettlements
                                    }
                                />

                                <div
                                    className={`${styles.main_center_panel} v-flex-container`}
                                >
                                    <TicketInfo
                                        selectedSettlements={
                                            selectedSettlements
                                        }
                                        selectedInstalments={
                                            selectedInstalments
                                        }
                                        handleCheckInstalment={
                                            handleCheckInstalment
                                        }
                                        ticketType={ticketType}
                                        ticket={currentTicket}
                                        color={currentCustom?.color?.current}
                                        currency={currencySymbol}
                                        setSelectedInstalments={
                                            setSelectedInstalments
                                        }
                                    />
                                    {currentTicket?.photos?.length ? (
                                        <PhotoPanel ticket={currentTicket} />
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            </div>
                        </main>
                    </>
                )
            )}
        </article>
    );
};

export default Search;
